import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Camera, Trash2, Loader2, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const Profile = () => {
  const { user, changePassword } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/auth/avatar`)
      .then(res => setAvatar(res.data.avatar))
      .catch(() => {});
  }, []);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no debe superar 2MB');
      return;
    }
    setAvatarLoading(true);
    try {
      const formData = new FormData();
      formData.append('imagen', file);
      const res = await axios.post(`${API}/auth/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAvatar(res.data.avatar);
      toast.success('Imagen de perfil actualizada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir imagen');
    }
    setAvatarLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteAvatar = async () => {
    try {
      await axios.delete(`${API}/auth/avatar`);
      setAvatar(null);
      toast.success('Imagen de perfil eliminada');
    } catch { toast.error('Error al eliminar imagen'); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Las contraseñas no coinciden'); return; }
    if (newPassword.length < 6) { toast.error('La contraseña debe tener al menos 6 caracteres'); return; }
    setLoading(true);
    const result = await changePassword(oldPassword, newPassword);
    if (result.success) {
      toast.success('Contraseña actualizada exitosamente');
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
    } else { toast.error(result.error); }
    setLoading(false);
  };

  const initials = getInitials(user?.full_name);

  return (
    <Layout>
      <div className="space-y-6" data-testid="profile-page">
        <div>
          <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight">Mi Perfil</h1>
          <p className="text-slate-600 mt-1">Información de tu cuenta</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Avatar + Info Card */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6 flex flex-col items-center">
            {/* Avatar */}
            <div className="relative group" data-testid="avatar-section">
              {avatar ? (
                <img src={avatar} alt="Avatar" className="w-28 h-28 rounded-full object-cover border-4 border-slate-200" data-testid="avatar-image" />
              ) : (
                <div className="w-28 h-28 rounded-full bg-primary flex items-center justify-center text-white text-3xl font-bold border-4 border-slate-200" data-testid="avatar-initials">
                  {initials}
                </div>
              )}
              {avatarLoading && (
                <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                  <Loader2 size={24} className="text-white animate-spin" />
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 bg-slate-800 hover:bg-slate-700 text-white rounded-full p-2 shadow-md transition-colors"
                title="Cambiar imagen"
                data-testid="upload-avatar-btn"
              >
                <Camera size={16} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleAvatarUpload}
                data-testid="avatar-file-input"
              />
            </div>
            {avatar && (
              <button
                onClick={handleDeleteAvatar}
                className="mt-2 text-xs text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors"
                data-testid="delete-avatar-btn"
              >
                <Trash2 size={12} /> Eliminar imagen
              </button>
            )}
            <h2 className="mt-4 text-lg font-semibold text-slate-900">{user?.full_name}</h2>
            <p className="text-sm text-slate-500">{user?.email}</p>
            <span className={`mt-2 inline-flex px-2.5 py-1 text-xs font-medium rounded-sm ${
              user?.role === 'MASTER' ? 'bg-purple-100 text-purple-800' :
              user?.role === 'ADMIN' ? 'bg-blue-100 text-blue-800' :
              'bg-green-100 text-green-800'
            }`}>
              {user?.role}
            </span>
          </div>

          {/* Info Personal */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">Datos Personales</h2>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">ID de Usuario</Label>
                <p className="text-sm text-slate-900 font-mono mt-0.5">{user?.id}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Nombre Completo</Label>
                <p className="text-sm text-slate-900 mt-0.5">{user?.full_name}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Email</Label>
                <p className="text-sm text-slate-900 mt-0.5">{user?.email}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">DNI</Label>
                <p className="text-sm text-slate-900 font-mono mt-0.5">{user?.dni}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Es Chofer</Label>
                <p className="text-sm text-slate-900 mt-0.5">{user?.is_driver ? 'Si' : 'No'}</p>
              </div>
            </div>
          </div>

          {/* Cambiar Contraseña */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">Cambiar Contraseña</h2>
            <form onSubmit={handleChangePassword} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Contraseña Actual</Label>
                <div className="relative">
                  <Input type={showOld ? 'text' : 'password'} value={oldPassword} onChange={e => setOldPassword(e.target.value)} required className="pr-10" data-testid="profile-old-password-input" />
                  <button type="button" onClick={() => setShowOld(!showOld)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>{showOld ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Nueva Contraseña</Label>
                <div className="relative">
                  <Input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="pr-10" data-testid="profile-new-password-input" />
                  <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>{showNew ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Confirmar Contraseña</Label>
                <div className="relative">
                  <Input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="pr-10" data-testid="profile-confirm-password-input" />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>{showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
              <Button type="submit" disabled={loading} data-testid="profile-change-password-submit" className="w-full bg-primary hover:bg-primary-hover text-white">
                {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
